/**
 * TradeHistoryPanel — closed trades across all agents with P&L.
 */

import { useAgentsOverview, useAgentHistory } from "@/sdk/hooks/use-agents";

export function TradeHistoryPanel() {
  const { data: overview, isError, isPending } = useAgentsOverview();
  const agentIds = overview?.agents ? Object.keys(overview.agents) : [];

  return (
    <div className="flex h-full flex-col bg-zinc-950">
      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-zinc-950">
            <tr className="border-b border-zinc-800 text-left text-zinc-500">
              <th className="px-3 py-1.5 font-medium">Agent</th>
              <th className="px-3 py-1.5 font-medium">Market</th>
              <th className="px-3 py-1.5 font-medium">Side</th>
              <th className="px-3 py-1.5 font-medium text-right">Size</th>
              <th className="px-3 py-1.5 font-medium text-right">Entry</th>
              <th className="px-3 py-1.5 font-medium text-right">Exit</th>
              <th className="px-3 py-1.5 font-medium text-right">P&L</th>
              <th className="px-3 py-1.5 font-medium">Reason</th>
              <th className="px-3 py-1.5 font-medium">Time</th>
            </tr>
          </thead>
          <tbody>
            {agentIds.map((id) => (
              <HistoryRows key={id} agentId={id} />
            ))}
          </tbody>
        </table>
        {/* Same honesty rule as PositionsPanel: an errored overview read is
            "unavailable", not an empty registry. No flash during initial
            load (isPending). */}
        {agentIds.length === 0 &&
          !isPending &&
          (isError ? (
            <div className="mt-8 text-center text-amber-400">
              Agent data unavailable — backend unreachable
            </div>
          ) : (
            <div className="mt-8 text-center text-zinc-600">No agents loaded</div>
          ))}
      </div>
    </div>
  );
}

function HistoryRows({ agentId }: { agentId: string }) {
  const { data } = useAgentHistory(agentId, 50);
  const trades = data?.trades ?? [];

  if (trades.length === 0) return null;

  return (
    <>
      {trades.map((t) => (
        <tr
          key={t.id}
          className="border-b border-zinc-800/30 hover:bg-zinc-900/50"
        >
          <td className="px-3 py-1.5 text-zinc-400">{agentId}</td>
          <td className="max-w-[180px] truncate px-3 py-1.5 text-zinc-300">
            {t.question}
          </td>
          <td className="px-3 py-1.5">
            <span className={`font-bold ${t.side === "BUY" ? "text-emerald-400" : "text-red-400"}`}>
              {t.side}
            </span>
          </td>
          <td className="px-3 py-1.5 text-right font-mono text-zinc-300">
            ${(t.size ?? 0).toFixed(0)}
          </td>
          <td className="px-3 py-1.5 text-right font-mono text-zinc-300">
            {(t.entry_price ?? 0).toFixed(2)}
          </td>
          <td className="px-3 py-1.5 text-right font-mono text-zinc-300">
            {(t.exit_price ?? 0).toFixed(2)}
          </td>
          <td className="px-3 py-1.5 text-right font-mono">
            <span className={(t.realized_pnl ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"}>
              {(t.realized_pnl ?? 0) >= 0 ? "+" : ""}${(t.realized_pnl ?? 0).toFixed(2)}
            </span>
          </td>
          <td className="px-3 py-1.5">
            <span className="rounded bg-zinc-800 px-1 py-0.5 text-[10px] text-zinc-400">
              {t.exit_reason}
            </span>
          </td>
          <td className="px-3 py-1.5 text-zinc-500">
            {new Date(t.exit_time).toLocaleString()}
          </td>
        </tr>
      ))}
    </>
  );
}
